/*
 * Purpose: Subscription tier SSOT — guest mode, plan gates, feature entitlements
 * Last modified: 2026-07-01 | Agent
 * Completeness: 100
 */
import type { PricingPlanId } from '@/lib/pricingPlans';

export type SubscriptionTier = PricingPlanId;
export type SubscriptionStatus = 'active' | 'guest' | 'none';

/** Max saved/runnable flows on Basic — aligns with pricing copy. */
export const BASIC_MAX_ACTIVE_MISSIONS = 3;

export type SubscriptionFeature =
  | 'save_flows'
  | 'run_flows'
  | 'scheduled_flows'
  | 'map_ai_assistant'
  | 'mission_rag'
  | 'integration_discovery'
  | 'admin_analytics'
  | 'live_runs_dashboard';

export type SubscriptionAccess = {
  tier: SubscriptionTier | null;
  status: SubscriptionStatus;
  guestMode: boolean;
  planSelected: boolean;
};

export function subscriptionFromState(
  state: Record<string, unknown> | null | undefined
): SubscriptionAccess {
  const guestMode = state?.guestMode === true;
  const rawTier = state?.subscriptionTier;
  const tier =
    rawTier === 'basic' || rawTier === 'plus' ? (rawTier as SubscriptionTier) : null;
  const status: SubscriptionStatus = guestMode
    ? 'guest'
    : tier
      ? 'active'
      : 'none';
  const planSelected = guestMode || tier != null;
  return { tier, status, guestMode, planSelected };
}

export function needsPlanSelection(access: SubscriptionAccess): boolean {
  return !access.planSelected;
}

const PLUS_ONLY: SubscriptionFeature[] = [
  'scheduled_flows',
  'map_ai_assistant',
  'mission_rag',
  'integration_discovery',
  'admin_analytics',
  'live_runs_dashboard',
];

const GUEST_BLOCKED: SubscriptionFeature[] = ['save_flows', 'run_flows', ...PLUS_ONLY];

export function canUseSubscriptionFeature(
  access: SubscriptionAccess,
  feature: SubscriptionFeature
): boolean {
  if (access.guestMode) {
    return !GUEST_BLOCKED.includes(feature);
  }
  if (!access.tier) return false;
  if (access.tier === 'plus') return true;
  if (PLUS_ONLY.includes(feature)) return false;
  return true;
}

export function canSaveFlows(access: SubscriptionAccess): boolean {
  return canUseSubscriptionFeature(access, 'save_flows');
}

export function canRunFlows(access: SubscriptionAccess): boolean {
  return canUseSubscriptionFeature(access, 'run_flows');
}

export function canCreateMission(
  access: SubscriptionAccess,
  activeMissionCount: number
): boolean {
  if (access.guestMode || !access.tier) return false;
  if (access.tier === 'plus') return true;
  return activeMissionCount < BASIC_MAX_ACTIVE_MISSIONS;
}

export function subscriptionGateMessage(
  feature: SubscriptionFeature,
  access: SubscriptionAccess
): string {
  if (access.guestMode) {
    return 'Guest mode — choose a plan to save or run flows.';
  }
  if (!access.tier) {
    return 'Choose a subscription plan to unlock this feature.';
  }
  if (access.tier === 'basic' && PLUS_ONLY.includes(feature)) {
    return 'Plus plan required for this feature.';
  }
  if (feature === 'save_flows' || feature === 'run_flows') {
    return 'Upgrade your plan to continue.';
  }
  return 'This feature is not available on your current plan.';
}
